/* Brüm Origen — global.js */
(function () {
  'use strict';

  const S = (window.BRUM && window.BRUM.strings) || {};

  /* ---------- utilidades ---------- */

  function sectionIdOf(el) {
    const sec = el.closest('.shopify-section');
    return sec ? sec.id.replace('shopify-section-', '') : null;
  }

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  // Anuncia a lectores de pantalla. Sin esto los cambios del carrito son mudos.
  function announce(message) {
    const region = document.querySelector('[data-live-region]');
    if (!region || !message) return;
    // Reasignar el mismo texto no dispara el anuncio; limpiar primero sí.
    region.textContent = '';
    setTimeout(() => { region.textContent = message; }, 60);
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.dataset.count = count;
    });
    if (S.cartCount) announce(S.cartCount.replace('__n__', count));
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  function replaceSectionHTML(targetEl, sectionHTML) {
    if (!targetEl || !sectionHTML) return;
    const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const fresh = doc.querySelector('[data-section-root]') || doc.body.firstElementChild;
    const current = targetEl.querySelector('[data-section-root]') || targetEl;
    if (fresh && current) current.innerHTML = fresh.innerHTML;
  }

  /* ---------- drawers genéricos ---------- */

  class XDrawer extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('.drawer__overlay');
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trap(e);
      });
      this.querySelectorAll('[data-drawer-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
    }
    // Cicla el tabulador dentro del panel: sin esto el foco se va detrás del
    // drawer y con teclado te quedas navegando una página que no ves.
    trap(e) {
      const panel = this.querySelector('.drawer__panel') || this;
      const items = [...panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    open() {
      this.opener = document.activeElement;
      this.classList.add('is-open');
      document.body.classList.add('drawer-open');
      this.setAttribute('aria-hidden', 'false');
      const panel = this.querySelector('.drawer__panel') || this;
      const focusable = panel.querySelector(FOCUSABLE);
      if (focusable) setTimeout(() => focusable.focus(), 120);
    }
    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('drawer-open');
      this.setAttribute('aria-hidden', 'true');
      // Devuelve el foco a quien lo abrió, no al inicio del documento.
      if (this.opener && document.contains(this.opener)) this.opener.focus();
    }
  }
  if (!customElements.get('x-drawer')) customElements.define('x-drawer', XDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.dataset.drawerOpen);
      if (drawer && drawer.open) {
        e.preventDefault();
        drawer.open();
      }
    }
  });

  /* ---------- header ---------- */

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 4);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
  }
  if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);

  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-search-toggle]');
    if (!t) return;
    const bar = document.getElementById('SearchBar');
    if (!bar) return;
    bar.classList.toggle('is-open');
    if (bar.classList.contains('is-open')) {
      const input = bar.querySelector('input[type="search"]');
      if (input) input.focus();
    }
  });

  /* ---------- announcement bar ---------- */

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.announcement__item')];
      if (this.items.length < 2) return;
      this.index = 0;
      this.querySelector('[data-ann-prev]')?.addEventListener('click', () => this.go(-1));
      this.querySelector('[data-ann-next]')?.addEventListener('click', () => this.go(1));
      this.start();
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', () => this.start());
    }
    start() {
      clearInterval(this.timer);
      this.timer = setInterval(() => this.go(1), 5000);
    }
    go(dir) {
      this.items[this.index].classList.remove('is-active');
      this.index = (this.index + dir + this.items.length) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- cantidad ---------- */

  class QtyInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const step = btn.dataset.qty === 'minus' ? -1 : 1;
          const min = parseInt(this.input.min || '1', 10);
          let next = Math.max(min, (parseInt(this.input.value, 10) || min) + step);
          /* Tope de preventa (piezas que quedan en la tanda) o de stock con
             «seguir vendiendo» prendido: lo pone el Liquid en `max`. Sin tope,
             el campo no trae `max` y esto no cambia nada. */
          const max = parseInt(this.input.max || '', 10);
          if (max > 0) next = Math.min(max, next);
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }
  if (!customElements.get('qty-input')) customElements.define('qty-input', QtyInput);

  /* ---------- carrito: drawer ---------- */

  class CartDrawer extends XDrawer {
    refreshFrom(sectionHTML) {
      const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const fresh = doc.querySelector('x-cart-drawer, cart-drawer-root, [data-drawer-content]');
      const source = fresh || doc.body;
      const freshInner = source.querySelector('.drawer__panel');
      const currentInner = this.querySelector('.drawer__panel');
      if (freshInner && currentInner) currentInner.innerHTML = freshInner.innerHTML;
      this.querySelectorAll('[data-drawer-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
    }
    // El aviso de «Agregamos N: son las que quedan» (avisoEnCajon) es de ese
    // agregado: al cerrar el cajón se va, para no encontrarlo la siguiente vez
    // que alguien abra el carrito desde el encabezado.
    close() {
      this.querySelectorAll('.drawer__aviso').forEach((el) => el.remove());
      super.close();
    }
  }
  if (!customElements.get('x-cart-drawer')) customElements.define('x-cart-drawer', CartDrawer);

  /* ---------- carrito: carrusel de sugerencias ---------- */

  /* Custom element y no listeners sueltos a propósito: refreshFrom() reemplaza el
     HTML del drawer en cada cambio del carrito, así que cualquier listener puesto
     sobre los botones moriría en el primer «agregar». connectedCallback vuelve a
     correr solo cuando el HTML nuevo entra al DOM. */
  class XsellRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-xs-track]');
      this.prev = this.querySelector('[data-xs-prev]');
      this.next = this.querySelector('[data-xs-next]');
      if (!this.track || !this.prev || !this.next) return;

      this.sync = () => {
        const t = this.track;
        const max = t.scrollWidth - t.clientWidth - 2;
        const desborda = t.scrollWidth > t.clientWidth + 4;
        /* Con pocas sugerencias no hay nada que desplazar: las flechas se
           esconden en vez de quedarse muertas. */
        this.prev.hidden = this.next.hidden = !desborda;
        this.prev.disabled = t.scrollLeft <= 2;
        this.next.disabled = t.scrollLeft >= max;
      };
      const paso = () => Math.max(this.track.clientWidth * 0.8, 140);
      this.prev.addEventListener('click', () => this.track.scrollBy({ left: -paso(), behavior: 'smooth' }));
      this.next.addEventListener('click', () => this.track.scrollBy({ left: paso(), behavior: 'smooth' }));
      this.track.addEventListener('scroll', this.sync, { passive: true });

      /* El drawer nace oculto: mide 0 de ancho hasta que se abre, y ahí las
         flechas se calculaban mal. ResizeObserver lo resuelve sin escuchar
         la apertura del cajón. */
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.sync);
        this.ro.observe(this.track);
      } else {
        window.addEventListener('resize', this.sync);
      }
      this.sync();
    }
    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
    }
  }
  if (!customElements.get('xsell-rail')) customElements.define('xsell-rail', XsellRail);

  /* ---------- riel genérico con flechas (Explorar colecciones) ---------- */

  /* Mismo patrón que xsell-rail, pero para secciones de portada: el paso es una
     tarjeta (ancho + gap) para que el snap caiga siempre en un borde limpio, y
     sin desborde las flechas se esconden con data-rail-static. */
  class BrumRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rail-track]');
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      if (!this.track || !this.prev || !this.next) return;

      this.sync = () => {
        const t = this.track;
        const max = t.scrollWidth - t.clientWidth - 2;
        this.toggleAttribute('data-rail-static', t.scrollWidth <= t.clientWidth + 4);
        this.prev.disabled = t.scrollLeft <= 2;
        this.next.disabled = t.scrollLeft >= max;
      };
      const paso = () => {
        const first = this.track.firstElementChild;
        if (!first) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return first.getBoundingClientRect().width + gap;
      };
      this.prev.addEventListener('click', () => this.track.scrollBy({ left: -paso(), behavior: 'smooth' }));
      this.next.addEventListener('click', () => this.track.scrollBy({ left: paso(), behavior: 'smooth' }));
      this.track.addEventListener('scroll', this.sync, { passive: true });
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.sync);
        this.ro.observe(this.track);
      } else {
        window.addEventListener('resize', this.sync);
      }
      this.sync();
    }
    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
    }
  }
  if (!customElements.get('brum-rail')) customElements.define('brum-rail', BrumRail);

  function getCartDrawer() { return document.getElementById('CartDrawer'); }

  /* ---------- carrito: líneas (drawer y página) ---------- */

  class CartLines extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-line-qty]');
        if (!input) return;
        /* Tope de preventa o de stock (el `max` que pone cart-line.liquid): una
           cantidad tecleada a mano no lo respeta sola, y Shopify la aceptaría
           con «seguir vendiendo» prendido. */
        let qty = parseInt(input.value, 10) || 0;
        const max = parseInt(input.max || '', 10);
        if (max > 0 && qty > max) { qty = max; input.value = String(max); }
        this.setLine(parseInt(input.dataset.line, 10), qty);
      });
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-line-remove]');
        if (btn) {
          e.preventDefault();
          this.setLine(parseInt(btn.dataset.line, 10), 0);
        }
      });
    }
    async setLine(line, quantity) {
      this.querySelectorAll('.cart-line').forEach((l) => l.classList.add('cart-line--loading'));
      const sections = ['cart-drawer'];
      /* Sin carrito de página, sectionIdOf(this) devuelve la sección del propio
         drawer, porque {% section 'cart-drawer' %} lo envuelve en
         #shopify-section-cart-drawer. Reemplazar esa sección destruía el
         <x-cart-drawer> —replaceSectionHTML hace innerHTML = fresh.innerHTML, así
         que el elemento se sustituye por sus hijos— y con él la clase is-open: el
         cajón se cerraba solo al cambiar la cantidad o quitar una línea. El drawer
         ya se actualiza aparte con refreshFrom. */
      let pageSection = sectionIdOf(document.querySelector('cart-lines[data-context="page"]') || this);
      if (pageSection === 'cart-drawer') pageSection = null;
      if (pageSection) sections.push(pageSection);
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: sections.join(',') }),
        });
        const cart = await res.json();
        updateCartCount(cart.item_count);
        const drawer = getCartDrawer();
        if (drawer && cart.sections && cart.sections['cart-drawer']) {
          drawer.refreshFrom(cart.sections['cart-drawer']);
        }
        if (pageSection && cart.sections && cart.sections[pageSection]) {
          const holder = document.getElementById('shopify-section-' + pageSection);
          if (holder) replaceSectionHTML(holder, cart.sections[pageSection]);
        }
      } catch (err) {
        window.location.reload();
      }
    }
  }
  if (!customElements.get('cart-lines')) customElements.define('cart-lines', CartLines);

  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-cart-note]')) {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: e.target.value }),
      });
    }
  });

  /* ---------- formulario de producto (AJAX add) ---------- */

  /* ---------- preventa mixta (9-oct) ----------
     Con existencia Y preventa (snippets/preventa.liquid, estado «m») la ficha
     dice cuántas hay para envío inmediato y, si piden más, desde cuándo se
     envían las demás. Al agregar, la compra se parte en DOS líneas: las que hay,
     sin propiedades, y el resto con las de la preventa («Preventa» y
     «_preventa_fecha»), contando lo que ya está en el carrito. Sin partirla,
     «seguir vendiendo» dejaba que 2 + 2 entraran como 4 normales con 2 en
     existencia: 2 piezas en preventa sin la fecha ni el aviso a atención. */
  function mixtaDe(root) {
    const el = (root || document).querySelector('[data-pv-mixta]');
    if (!el || el.hidden) return null;
    const stock = parseInt(el.dataset.stock, 10) || 0;
    const piezas = parseInt(el.dataset.piezas, 10) || 0;
    if (stock <= 0 || piezas <= 0 || !el.dataset.fecha) return null;
    return { el, stock, piezas, fecha: el.dataset.fecha, fechaTxt: el.dataset.fechaTxt || '', texto: el.dataset.texto || '' };
  }

  function llenaPlantilla(tpl, vals) {
    return String(tpl || '').replace(/\{(\w+)\}/g, (m, k) => (k in vals ? String(vals[k]) : m));
  }

  /* La frase de debajo del botón, según la cantidad: «Hay 2 para envío
     inmediato…» o, con más de las que hay, «De tus 4: 2 con envío inmediato y
     2 a partir del…». Es la cuenta de la ficha; la del carrito, al agregar,
     además descuenta lo que ya está en él. */
  function pintaMixta(qty, root) {
    const m = mixtaDe(root);
    if (!m) return;
    const total = Math.max(1, parseInt(qty, 10) || 1);
    m.el.textContent = total > m.stock
      ? llenaPlantilla(m.el.dataset.tplPartida, { total, ya: m.stock, despues: total - m.stock, fecha: m.fechaTxt })
      : llenaPlantilla(m.el.dataset.tpl, { n: m.stock, fecha: m.fechaTxt });
  }

  /* Cómo se parte una compra mixta, contra lo que ya está en el carrito: primero
     lo que queda de existencia (sin propiedades) y lo demás en preventa, hasta
     las piezas de la tanda. `null` si el carrito no se pudo leer: sin saber qué
     hay en él no se parte (se podría vender en existencia lo que ya no hay). */
  function partirMixta(m, carrito, id, pedidas, extra) {
    if (!carrito || !Array.isArray(carrito.items)) return null;
    let normales = 0;
    let enPreventa = 0;
    carrito.items.forEach((it) => {
      if (it.variant_id !== id) return;
      if (it.properties && it.properties._preventa_fecha) enPreventa += it.quantity;
      else normales += it.quantity;
    });
    const ya = Math.min(pedidas, Math.max(0, m.stock - normales));
    const despues = Math.min(pedidas - ya, Math.max(0, m.piezas - enPreventa));
    const conExtra = extra && Object.keys(extra).length ? extra : null;
    const items = [];
    if (ya > 0) items.push(conExtra ? { id, quantity: ya, properties: { ...conExtra } } : { id, quantity: ya });
    if (despues > 0) items.push({ id, quantity: despues, properties: { ...(conExtra || {}), Preventa: m.texto, _preventa_fecha: m.fecha } });
    return { items, pedidas, agregadas: ya + despues };
  }

  /* El tope de la mixta (lo que hay + las de la preventa) lo pone el JS: el
     campo sale del Liquid con el del stock, para que sin JS el envío nativo no
     mande como normales las piezas de la preventa (revisión del 9-oct). */
  function topeMixta(root) {
    const m = mixtaDe(root);
    const qty = (root || document).querySelector('input[name="quantity"]');
    if (m && qty) qty.max = String(m.stock + m.piezas);
  }

  /* «Agregamos N: son las que quedan» también arriba del carrito: el aviso de
     la ficha queda detrás de la cortina mientras el cajón está abierto. Se va
     al cerrar el cajón (CartDrawer.close) o con el siguiente cambio del
     carrito, que lo vuelve a pintar; y nunca hay dos. */
  function avisoEnCajon(drawer, texto) {
    const head = drawer && drawer.querySelector('.drawer__panel .drawer__head');
    if (!head) return;
    drawer.querySelectorAll('.drawer__aviso').forEach((el) => el.remove());
    const p = document.createElement('p');
    p.className = 'drawer__aviso';
    p.setAttribute('role', 'status');
    p.textContent = texto;
    head.insertAdjacentElement('afterend', p);
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      // Sella la rama de la prueba A/B en la línea del carrito. Viaja hasta el
      // pedido, así que la conversión de cada rama se cuenta desde el Admin sin
      // app ni pixel. El campo solo existe en las fichas que participan.
      const abProp = this.form.querySelector('[data-ab-prop]');
      if (abProp && window.BRUM_AB_FOTO) abProp.value = window.BRUM_AB_FOTO;

      this.form.addEventListener('submit', (e) => this.submit(e));

      // Preventa mixta: la frase de debajo del botón sigue a la cantidad, y el
      // tope sube a lo que hay más la preventa ahora que hay quien parta.
      const qty = this.form.querySelector('input[name="quantity"]');
      if (qty) ['change', 'input'].forEach((t) => qty.addEventListener(t, () => pintaMixta(qty.value, this)));
      topeMixta(this);
    }
    async submit(e) {
      const drawer = getCartDrawer();
      const mixta = mixtaDe(this);
      const pagina = !drawer || (window.BRUM && window.BRUM.cartType === 'page');
      // Envío nativo, salvo la mixta: ésa siempre se parte aquí.
      if (pagina && !mixta) return;
      e.preventDefault();
      // Un envío a la vez. Dos clics seguidos leían el mismo carrito antes de
      // que entrara el primero y lo partían igual dos veces: con 2 en
      // existencia, 2 + 2 terminaban como 4 normales (revisión del 9-oct).
      if (this.enviando) return;
      this.enviando = true;
      const buttons = [...this.form.querySelectorAll('[type="submit"]'), ...document.querySelectorAll(`[type="submit"][form="${this.form.id}"]`)];
      buttons.forEach((b) => { b.classList.add('is-loading'); b.setAttribute('aria-disabled', 'true'); });
      const msg = this.querySelector('[data-form-error]');
      if (msg) msg.hidden = true;
      try {
        let res;
        let aviso = '';
        if (mixta) {
          const id = parseInt((this.form.querySelector('[name="id"]') || {}).value, 10);
          const qtyInput = this.form.querySelector('input[name="quantity"]');
          const pedidas = Math.max(1, parseInt(qtyInput ? qtyInput.value : '1', 10) || 1);
          const ab = this.form.querySelector('[data-ab-prop]');
          const extra = ab && ab.value ? { _ab_foto: ab.value } : null;
          const parte = partirMixta(mixta, await fetchCart(), id, pedidas, extra);
          if (!parte) throw new Error('No pudimos leer tu carrito. Intenta de nuevo.');
          if (!parte.items.length) throw new Error(mixta.el.dataset.txtTope || 'Ya tienes en tu carrito todas las piezas que quedan.');
          if (parte.agregadas < parte.pedidas) {
            aviso = parte.agregadas === 1 && mixta.el.dataset.txtParcialUno
              ? mixta.el.dataset.txtParcialUno
              : llenaPlantilla(mixta.el.dataset.txtParcial, { n: parte.agregadas });
          }
          res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: parte.items, sections: 'cart-drawer', sections_url: window.location.pathname }),
          });
        } else {
          const data = new FormData(this.form);
          data.append('sections', 'cart-drawer');
          data.append('sections_url', window.location.pathname);
          res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: data,
          });
        }
        const json = await res.json();
        if (json.status && json.status !== 200) throw new Error(json.description || 'error');
        if (pagina) {
          window.location.href = '/cart';
          return;
        }
        if (json.sections && json.sections['cart-drawer']) drawer.refreshFrom(json.sections['cart-drawer']);
        const cart = await fetchCart();
        updateCartCount(cart.item_count);
        drawer.open();
        if (aviso) {
          if (msg) { msg.textContent = aviso; msg.hidden = false; }
          avisoEnCajon(drawer, aviso);
        }
      } catch (err) {
        if (msg) { msg.textContent = err.message; msg.hidden = false; }
      } finally {
        this.enviando = false;
        buttons.forEach((b) => { b.classList.remove('is-loading'); b.removeAttribute('aria-disabled'); });
      }
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* ---------- selector de variantes ---------- */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-variants]');
      if (!dataEl) return;
      this.variants = JSON.parse(dataEl.textContent);
      this.addEventListener('change', () => this.onChange());
      this.onChange(true);
    }
    selectedOptions() {
      const opts = [];
      this.querySelectorAll('fieldset').forEach((set) => {
        const checked = set.querySelector('input:checked');
        opts.push(checked ? checked.value : null);
      });
      return opts;
    }
    currentVariant() {
      const sel = this.selectedOptions();
      return this.variants.find((v) => v.options.every((o, i) => o === sel[i]));
    }
    onChange(initial) {
      // estado visual de pills
      this.querySelectorAll('.opt__pill').forEach((pill) => {
        const input = pill.querySelector('input');
        pill.classList.toggle('is-active', input.checked);
      });
      this.querySelectorAll('fieldset').forEach((set) => {
        const label = set.querySelector('[data-opt-value]');
        const checked = set.querySelector('input:checked');
        if (label && checked) label.textContent = checked.value;
      });
      this.markAvailability();

      const variant = this.currentVariant();
      const root = document;
      const idInput = root.querySelector('[data-variant-id]');
      const errEl = root.querySelector('[data-form-error]');
      if (errEl) errEl.hidden = true;

      if (!variant) {
        this.setBuyState(false, S.unavailable || 'No disponible');
        this.syncPreventa({ max: null }, null, false);
        this.syncAvisame(null);
        return;
      }
      if (idInput) idInput.value = variant.id;

      // precio
      root.querySelectorAll('[data-price-current]').forEach((el) => { el.textContent = variant.price_formatted; });
      root.querySelectorAll('[data-price-compare]').forEach((el) => {
        el.textContent = variant.compare_formatted || '';
        el.hidden = !variant.compare_formatted;
      });
      root.querySelectorAll('[data-price-wrap]').forEach((el) => {
        el.classList.toggle('price--sale', !!variant.compare_formatted);
      });

      /* Preventa (24-sep). `vendible` y no `available`: con «seguir vendiendo»
         prendido Shopify dice que sí aunque no haya fecha que prometer, y esa
         variante se enseña agotada. JSON viejo en caché (sin `vendible`) cae a
         `available`, que era el comportamiento de antes. */
      const pv = variant.preventa || null;
      const vendible = typeof variant.vendible === 'boolean' ? variant.vendible : variant.available;
      const label = pv ? (S.preorder || 'Comprar en preventa')
        : (vendible ? (S.addToCart || 'Agregar al carrito') : (S.soldOut || 'Agotado'));
      this.setBuyState(vendible, label);
      this.syncPreventa(variant, pv, vendible);
      this.syncAvisame(variant);

      // media
      if (variant.media_id) {
        const slide = root.querySelector(`.gallery__slide[data-media-id="${variant.media_id}"]`);
        const gal = root.querySelector('media-gallery');
        if (slide && !initial && gal && gal.goTo) gal.goTo(slide, true);
      }
      // URL
      if (!initial && variant.id) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
    markAvailability() {
      const sel = this.selectedOptions();
      this.querySelectorAll('fieldset').forEach((set, idx) => {
        set.querySelectorAll('input').forEach((input) => {
          const test = [...sel];
          test[idx] = input.value;
          const match = this.variants.find((v) => v.options.every((o, i) => test[i] === null || o === test[i]));
          const pill = input.closest('.opt__pill');
          const ok = match && (typeof match.vendible === 'boolean' ? match.vendible : match.available);
          if (pill) pill.classList.toggle('is-unavailable', !ok);
        });
      });
    }
    /* Todo lo que cambia con la variante cuando hay preventa de por medio: las
       dos propiedades de línea (se envían sólo si la variante es preventa), el
       tope de cantidad, la pastilla de «En stock»/«Preventa» y la ventana de
       entrega, que en preventa se cuenta desde la llegada al almacén. */
    syncPreventa(variant, pv, vendible) {
      const root = document;
      root.querySelectorAll('[data-pv-texto]').forEach((el) => {
        el.value = pv ? pv.texto : '';
        el.disabled = !pv;
      });
      root.querySelectorAll('[data-pv-fecha]').forEach((el) => {
        el.value = pv ? pv.fecha : '';
        el.disabled = !pv;
      });
      root.querySelectorAll('product-form input[name="quantity"]').forEach((input) => {
        const max = parseInt(variant.max, 10);
        if (max > 0) {
          input.max = String(max);
          if ((parseInt(input.value, 10) || 1) > max) input.value = String(max);
        } else {
          input.removeAttribute('max');
        }
      });
      root.querySelectorAll('[data-stock-pill]').forEach((pill) => {
        pill.hidden = !vendible;
        pill.classList.toggle('stock-pill--preventa', !!pv);
        pill.textContent = pv ? (pill.dataset.txtPreventa || 'Preventa') : (pill.dataset.txtStock || 'En stock');
      });
      root.querySelectorAll('brum-entrega').forEach((el) => {
        el.hidden = !vendible;
        if (pv) el.dataset.desde = pv.fecha;
        else delete el.dataset.desde;
        try {
          el.pinta();
        } catch (e) {
          el.textContent = el.dataset.respaldo || '';
        }
      });
      /* Preventa mixta (9-oct): la frase de debajo del botón y lo que usa el
         carrito para partir la compra. La entrega de arriba es la de lo que hay
         (sin `desde`): lo que va en preventa trae su fecha en la frase. */
      const mx = variant && variant.mixta ? variant.mixta : null;
      root.querySelectorAll('[data-pv-mixta]').forEach((el) => {
        el.hidden = !mx;
        el.dataset.stock = mx ? String(mx.stock) : '0';
        el.dataset.piezas = mx ? String(mx.piezas) : '0';
        el.dataset.fecha = mx ? mx.fecha : '';
        el.dataset.fechaTxt = mx ? mx.fecha_txt || '' : '';
        el.dataset.texto = mx ? mx.texto || '' : '';
        if (!mx) el.textContent = '';
      });
      if (mx) {
        const q = root.querySelector('product-form input[name="quantity"]');
        pintaMixta(q ? q.value : 1);
      }
    }
    setBuyState(enabled, label) {
      document.querySelectorAll('[data-atc]').forEach((btn) => {
        btn.toggleAttribute('disabled', !enabled);
        const l = btn.querySelector('.btn__label');
        if (l) l.textContent = label;
      });
    }
    /* Avísame (28-sep): se prende en los colores agotados que van a volver
       (`avisable`, calculado en Liquid por snippets/avisame-puede.liquid) y se
       apaga en los demás. Antes de que <brum-avisame> exista (primera pasada al
       cargar) no hace falta: Liquid ya lo pintó para la variante que abre. */
    syncAvisame(variant) {
      document.querySelectorAll('brum-avisame').forEach((el) => {
        if (typeof el.sync === 'function') el.sync(variant);
      });
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---------- avísame cuando vuelva (28-sep) ---------- */

  /* El navegador suscribe al cliente directo en Klaviyo, al «back in stock» de
     ESA variante, con la llave PÚBLICA de la cuenta (la misma que ya carga
     klaviyo.js en toda la tienda). Es el endpoint de cliente que Klaviyo
     documenta para formularios propios: responde 202 sin cuerpo cuando acepta.
     El correo lo manda el flow «Aviso de disponibilidad (Back in Stock)» cuando
     Shopify vuelve a tener al menos 1 pieza. La misma suscripción, con el mismo
     formato, es la que registra el bot (brum-chatbot, api/waitlist.js).
     Qué variantes lo ofrecen: snippets/avisame-puede.liquid. Todo: docs/avisame.md. */
  const AVISAME_URL = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=';
  const AVISAME_REVISION = '2024-10-15';
  const AVISAME_PREFIJO = '$shopify:::$default:::';
  const AVISAME_LLAVE = 'brum:avisos';
  const correoValido = (s) => /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(String(s || '').trim());

  /* Las variantes que ya se pidieron desde este navegador, para no volver a
     enseñar el formulario al regresar a la ficha. Solo ids, nunca el correo. */
  function avisosPedidos() {
    try {
      const v = JSON.parse(localStorage.getItem(AVISAME_LLAVE) || '[]');
      return Array.isArray(v) ? v.map(String) : [];
    } catch (e) {
      return [];
    }
  }
  function guardarAvisoPedido(id) {
    try {
      const v = [String(id)].concat(avisosPedidos().filter((x) => x !== String(id))).slice(0, 40);
      localStorage.setItem(AVISAME_LLAVE, JSON.stringify(v));
    } catch (e) { /* modo privado o almacenamiento bloqueado: se ignora */ }
  }

  class BrumAvisame extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-avisame-form]');
      if (!this.form) return;
      this.input = this.form.querySelector('input[type="email"]');
      this.trampa = this.form.querySelector('input[name="sitio"]');
      this.boton = this.form.querySelector('[type="submit"]');
      this.estado = this.querySelector('[data-avisame-estado]');
      this.texto = this.querySelector('[data-avisame-texto]');
      this.otro = this.querySelector('[data-avisame-otro]');
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.enviar();
      });
      /* «Avisar a otro correo»: vuelve a abrir el formulario vacío. */
      if (this.otro) {
        this.otro.addEventListener('click', () => {
          this.form.hidden = false;
          this.muestra(null);
          if (this.input) {
            this.input.value = '';
            this.input.focus();
          }
        });
      }
      this.pinta();
    }
    /* Lo llama el selector de variantes en cada cambio de color o medida. */
    sync(variant) {
      const puede = !!(variant && variant.avisable);
      this.hidden = !puede;
      if (!puede) return;
      this.dataset.variantId = String(variant.id);
      this.dataset.variante = variant.titulo && variant.titulo !== 'Default Title' ? variant.titulo : '';
      this.pinta();
    }
    conVariante(base, conV) {
      const v = this.dataset.variante || '';
      return v && conV ? conV.replace('__v__', v) : base;
    }
    pinta() {
      if (this.texto) this.texto.textContent = this.conVariante(this.dataset.txtTexto, this.dataset.txtTextoV);
      const pedido = avisosPedidos().includes(String(this.dataset.variantId));
      this.form.hidden = pedido;
      this.muestra(pedido ? 'ok' : null);
    }
    muestra(tipo, texto) {
      if (this.otro) this.otro.hidden = tipo !== 'ok';
      if (!this.estado) return;
      if (!tipo) {
        this.estado.hidden = true;
        this.estado.textContent = '';
        return;
      }
      this.estado.textContent = texto || this.conVariante(this.dataset.txtOk, this.dataset.txtOkV);
      this.estado.className = `avisame__estado avisame__estado--${tipo}`;
      this.estado.hidden = false;
    }
    ocupado(si) {
      if (!this.boton) return;
      this.boton.classList.toggle('is-loading', si);
      this.boton.disabled = si;
    }
    listo(id) {
      guardarAvisoPedido(id);
      this.form.hidden = true;
      this.muestra('ok');
    }
    async enviar() {
      const correo = String((this.input && this.input.value) || '').trim();
      if (!correoValido(correo)) {
        this.muestra('error', this.dataset.txtCorreo);
        if (this.input) this.input.focus();
        return;
      }
      const id = this.dataset.variantId;
      if (this.trampa && this.trampa.value) { this.listo(id); return; }
      const llave = this.dataset.klaviyo;
      if (!id || !llave) { this.muestra('error', this.dataset.txtError); return; }

      this.ocupado(true);
      this.muestra(null);
      try {
        const res = await fetch(AVISAME_URL + encodeURIComponent(llave), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            Accept: 'application/vnd.api+json',
            revision: AVISAME_REVISION,
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: { data: { type: 'profile', attributes: { email: correo } } },
              },
              relationships: {
                variant: { data: { type: 'catalog-variant', id: AVISAME_PREFIJO + id } },
              },
            },
          }),
          signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(12000) : undefined,
        });
        if (res.ok) {
          this.listo(id);
          return;
        }
        /* Klaviyo rechaza con 400 un correo que no le parece válido: ese sí es
           culpa del cliente y se le dice; cualquier otra cosa es nuestra. */
        const cuerpo = await res.text().catch(() => '');
        const esCorreo = res.status === 400 && /email/i.test(cuerpo);
        this.muestra('error', esCorreo ? this.dataset.txtCorreo : this.dataset.txtError);
      } catch (err) {
        this.muestra('error', this.dataset.txtError);
      } finally {
        this.ocupado(false);
      }
    }
  }
  if (!customElements.get('brum-avisame')) customElements.define('brum-avisame', BrumAvisame);

  /* ---------- encuadre de la foto en la tarjeta ---------- */

  /* Las fotos del catálogo son cuadradas y el marco de la tarjeta es 3:4: con
     `object-fit: cover` se recortaban los costados (mesas de 200 cm, bancas,
     mesas laterales altas). Y el aire alrededor de la pieza va de 0 a 26 %
     según la foto, así que con `contain` a secas una mesa sale enorme y una
     silla diminuta.

     Aquí se mide, en una copia chica de la foto (120 px), el recuadro que ocupa
     la pieza sobre el fondo del estudio, y se escala y centra la imagen para
     que la pieza ocupe siempre la misma fracción del marco. La medida se
     cachea por foto en localStorage y es proporcional, así que vale para
     cualquier tamaño de la misma imagen.

     El marco es el gris del estudio (#F7F6F4) para todas. Una foto sobre
     blanco se funde con él (`multiply`, en base.css), y las que traen fondo
     propio —las del estudio y algunas viejas que ya venían sobre gris— pintan
     el marco de ese mismo color: así ninguna deja un recuadro al achicarse y
     la rejilla se ve pareja.

     Es mejora progresiva: sin JS la foto queda completa sobre el gris. Una foto
     de ambiente —fondo que no es claro y liso— no se toca: se marca
     is-lifestyle y vuelve a `cover`. */

  const fitOn = document.documentElement.classList.contains('card-fit-contain');
  const FIT_TARGET = Math.min(0.96, Math.max(0.6, (parseInt(document.documentElement.dataset.cardFitSize, 10) || 85) / 100));
  const FIT_AREA = Math.min(0.9, Math.max(0.2, (parseInt(document.documentElement.dataset.cardFitArea, 10) || 45) / 100));
  // v2 guarda también el color del fondo de cada foto; la v1 no lo tenía.
  const FIT_CACHE_KEY = 'brum:fit:v2';
  let fitCache = {};
  try { fitCache = JSON.parse(localStorage.getItem(FIT_CACHE_KEY) || '{}'); } catch (e) { /* modo privado */ }
  try { localStorage.removeItem('brum:fit:v1'); } catch (e) { /* modo privado */ }

  let fitPersistTimer;
  function persistFitCache() {
    clearTimeout(fitPersistTimer);
    fitPersistTimer = setTimeout(() => {
      try {
        const keys = Object.keys(fitCache);
        if (keys.length > 900) keys.slice(0, keys.length - 900).forEach((k) => { delete fitCache[k]; });
        localStorage.setItem(FIT_CACHE_KEY, JSON.stringify(fitCache));
      } catch (e) { /* cuota llena */ }
    }, 500);
  }

  // Ruta + versión, sin parámetros de tamaño: la medida es la misma para la
  // foto de 360, 540 o 720 px. Y sin el esquema: antes de cargar, la foto se
  // lee del atributo (`//brum.mx/…`) y después de `currentSrc` (`https://…`);
  // con esquema, la medida guardada casi nunca se encontraba antes de cargar
  // y la foto se veía un instante sin encuadrar.
  function fitKeyOf(src) {
    src = src.replace(/^https?:/, '');
    const q = src.indexOf('?');
    const path = q < 0 ? src : src.slice(0, q);
    const v = q < 0 ? null : (src.slice(q + 1).match(/(?:^|&)v=(\d+)/) || [])[1];
    return v ? path + '?v=' + v : path;
  }

  // La copia chica para medir: se pide al CDN con width=120.
  function fitProbeSrcOf(src) {
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&]width=)\d+/, '$1120').replace(/[?&]height=\d+/, '');
    return src;
  }

  /* Devuelve el recuadro de la pieza como fracciones de la foto y el color de
     su fondo (bg), o life=1 si es una foto de ambiente. null si el canvas no se
     puede leer (sin CORS). */
  function measurePackshot(img) {
    const W = 96;
    const H = Math.max(8, Math.round(W * img.naturalHeight / img.naturalWidth));
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, W, H);
    let d;
    try {
      d = ctx.getImageData(0, 0, W, H).data;
    } catch (e) {
      return null;
    }
    // Un píxel transparente (PNG recortado) cuenta como fondo blanco.
    const px = (i) => (d[i + 3] < 128 ? [255, 255, 255] : [d[i], d[i + 1], d[i + 2]]);
    const lum = (c) => 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];

    // 1. El fondo se lee en el anillo exterior. Se toma la MEDIANA de
    //    luminosidad y no el promedio: una mesa de 200 cm sin aire toca los
    //    bordes izquierdo y derecho, y con el promedio el anillo dejaba de ser
    //    «blanco liso» y la foto caía como ambiente, justo la que más importa
    //    no recortar. Es ambiente si el fondo no es claro o si menos del 60 %
    //    del anillo coincide con él (gradientes, paredes, pisos).
    const ring = [];
    for (let x = 0; x < W; x++) ring.push(x, (H - 1) * W + x);
    for (let y = 1; y < H - 1; y++) ring.push(y * W, y * W + W - 1);
    const ringPx = ring.map((p) => px(p * 4));
    const lums = ringPx.map(lum).sort((a, b) => a - b);
    const lmed = lums[Math.floor(lums.length / 2)];
    const sum = [0, 0, 0];
    let n = 0;
    ringPx.forEach((c) => {
      if (Math.abs(lum(c) - lmed) > 8) return;
      sum[0] += c[0]; sum[1] += c[1]; sum[2] += c[2]; n++;
    });
    const bg = [sum[0] / n, sum[1] / n, sum[2] / n];
    const near = ringPx.filter((c) => Math.max(Math.abs(c[0] - bg[0]), Math.abs(c[1] - bg[1]), Math.abs(c[2] - bg[2])) <= 20).length;
    if (lum(bg) < 222 || near / ringPx.length < 0.6) return { life: 1 };

    // 2. La pieza es todo lo que se aparta del fondo.
    let x0 = W, y0 = H, x1 = -1, y1 = -1;
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        const c = px((y * W + x) * 4);
        const diff = Math.max(Math.abs(c[0] - bg[0]), Math.abs(c[1] - bg[1]), Math.abs(c[2] - bg[2]));
        if (diff <= 20) continue;
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
    const fondo = bg.map((v) => Math.round(v));
    if (x1 < 0) return { life: 0, x: 0, y: 0, w: 1, h: 1, bg: fondo };
    const r = (v) => Math.round(v * 1000) / 1000;
    return { life: 0, x: r(x0 / W), y: r(y0 / H), w: r((x1 - x0 + 1) / W), h: r((y1 - y0 + 1) / H), bg: fondo };
  }

  // Los asientos (sillas, sillones, bancas, bancos y taburetes) traen su propio
  // tamaño en la tarjeta (data-fit-size, ver product-card.liquid); el resto
  // usa el ajuste general.
  function fitTargetOf(img) {
    const card = img.closest('.card');
    const v = card ? parseInt(card.dataset.fitSize, 10) : NaN;
    return v ? Math.min(0.96, Math.max(0.6, v / 100)) : FIT_TARGET;
  }

  /* El color del marco. Lo decide la foto principal y no la de hover, porque
     comparten marco. Fondo blanco: se deja el gris del CSS y la foto se funde
     con `multiply`. Otro fondo claro: el marco toma ese color y la foto se
     pinta normal (.is-fondo-propio), para que al achicarla no quede recuadro. */
  function paintFitFrame(img, bg) {
    const propio = !!bg && !bg.every((v) => v >= 250);
    img.classList.toggle('is-fondo-propio', propio);
    const box = img.closest('.card__media');
    if (box && !img.classList.contains('card__img--hover')) {
      box.style.backgroundColor = propio ? `rgb(${bg.join(',')})` : '';
    }
  }

  function applyFit(img, m) {
    if (m.life) {
      img.classList.add('is-lifestyle');
      paintFitFrame(img, null);
      img.style.removeProperty('--fit-s');
      img.style.removeProperty('--fit-x');
      img.style.removeProperty('--fit-y');
      img.dataset.fit = 'done';
      return;
    }
    img.classList.remove('is-lifestyle');
    paintFitFrame(img, m.bg);
    const W = img.naturalWidth || parseInt(img.getAttribute('width'), 10);
    const H = img.naturalHeight || parseInt(img.getAttribute('height'), 10);
    // El marco y no el padre: la foto de hover va dentro de un <picture> con
    // `display: contents`, que mide 0 × 0, y se quedaba siempre en «wait».
    const box = img.closest('.card__media') || img.parentElement;
    const cw = box ? box.clientWidth : 0;
    const ch = box ? box.clientHeight : 0;
    if (!W || !H || !cw || !ch) {
      // Tarjeta oculta (tope de móvil) o sin medidas: se reintenta al cambiar
      // el tamaño de la ventana.
      img.dataset.fit = 'wait';
      return;
    }
    // Con `contain`, la foto se pinta a escala k centrada en el marco.
    const k = Math.min(cw / W, ch / H);
    const dw = W * k, dh = H * k;
    // La pieza debe ocupar el tamaño de su tarjeta en su lado limitante.
    const T = fitTargetOf(img);
    let s = Math.min(T * cw / (m.w * dw), T * ch / (m.h * dh));
    // Y, si no es un asiento, no más superficie que el tope (Ajustes →
    // «Superficie máxima de la pieza»). Una mesa redonda o un módulo casi
    // cuadrados llegaban al tamaño por el ancho y llenaban también el alto; una
    // mesa alta o un espejo, al revés. Los asientos ya van más chicos.
    const card = img.closest('.card');
    if (!(card && card.dataset.fitSize)) {
      const a = (m.w * dw / cw) * (m.h * dh / ch);
      if (a > 0) s = Math.min(s, Math.sqrt(FIT_AREA / a));
    }
    s = Math.max(0.75, Math.min(2, s));
    // Y quedar centrada: se corrige el centro de la pieza respecto al de la foto.
    // El translate va en % del propio elemento para que no dependa del ancho.
    const cx = (m.x + m.w / 2 - 0.5) * dw;
    const cy = (m.y + m.h / 2 - 0.5) * dh;
    const tx = (-cx * s / cw) * 100;
    const ty = (-cy * s / ch) * 100;
    // Sin transición al aplicar: el encuadre no debe verse como un zoom. El
    // reflow forzado (offsetWidth) confirma el transform nuevo ANTES de devolver
    // la transición; sin él, el rAF corre antes del recálculo de estilos y el
    // navegador animaba el encuadre de todos modos.
    img.style.transition = 'none';
    img.style.setProperty('--fit-s', s.toFixed(3));
    img.style.setProperty('--fit-x', tx.toFixed(2) + '%');
    img.style.setProperty('--fit-y', ty.toFixed(2) + '%');
    img.dataset.fit = 'done';
    void img.offsetWidth;
    requestAnimationFrame(() => { img.style.transition = ''; });
  }

  function probeAndFit(img) {
    const src = img.currentSrc || img.src;
    if (!src) return;
    // En un táctil, la foto de hover resuelve a un gif de 1px en línea (ver
    // product-card.liquid). Sondearlo es una petición y un canvas por tarjeta
    // para medir un píxel transparente, y lo que devuelve `measurePackshot`
    // sobre eso no significa nada — pero se guardaría en la caché de encuadre.
    if (src.startsWith('data:')) return;
    const key = fitKeyOf(src);
    if (fitCache[key]) { applyFit(img, fitCache[key]); return; }
    if (navigator.connection && navigator.connection.saveData) return;
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.decoding = 'async';
    probe.onload = () => {
      const m = measurePackshot(probe);
      if (!m) return;
      fitCache[key] = m;
      persistFitCache();
      // La tarjeta pudo cambiar de foto mientras se medía (muestra de color).
      if (fitKeyOf(img.currentSrc || img.src) === key) applyFit(img, m);
    };
    probe.src = fitProbeSrcOf(src);
  }

  /* Encuadra una foto de tarjeta. Se llama al pintar la rejilla y cada vez que
     una muestra de color cambia la foto. Con la medida en caché se aplica antes
     de que la foto cargue, así no hay salto.

     Las del estudio (estudio-…) también se encuadran. Vienen en 3:4, pero la
     pieza ocupa entre 70 y 82 % del marco según la foto, y junto a las viejas,
     que quedan al 86 %, se veían chicas. El marco blanco que les salía a los
     sofás al achicarlos ya no sale: el marco toma el gris de su fondo. */
  function fitCardImage(img) {
    if (!fitOn || !img) return;
    const src = img.currentSrc || img.getAttribute('src');
    if (src && fitCache[fitKeyOf(src)]) {
      applyFit(img, fitCache[fitKeyOf(src)]);
      return;
    }
    // Foto nueva en la tarjeta (otra muestra de color): mientras se mide, el
    // marco vuelve al gris del CSS en vez de quedarse con el de la anterior.
    paintFitFrame(img, null);
    if (img.complete && img.naturalWidth) probeAndFit(img);
    else img.addEventListener('load', () => probeAndFit(img), { once: true });
  }

  function initCardFit(root) {
    if (!fitOn) return;
    // Sin hover, la foto de hover no se enseña nunca: medirla es trabajo de CPU
    // (una petición de sondeo + un canvas por tarjeta) para un encuadre que
    // nadie va a ver, y encima en el dispositivo más lento. Se mira en cada
    // llamada y no una sola vez al cargar, porque la rejilla se vuelve a pintar
    // con el scroll infinito y el filtro.
    const conHover = window.matchMedia('(hover: hover)').matches;
    const sel = conHover
      ? '.card__img:not([data-fit])'
      : '.card__img:not([data-fit]):not(.card__img--hover)';
    (root || document).querySelectorAll(sel).forEach((img) => {
      img.dataset.fit = '1';
      fitCardImage(img);
    });
  }

  if (fitOn) {
    let fitResizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(fitResizeTimer);
      fitResizeTimer = setTimeout(() => {
        document.querySelectorAll('.card__img[data-fit="wait"]').forEach((img) => fitCardImage(img));
      }, 250);
    });
  }

  /* ---------- color real de las muestras ---------- */

  /* Un nombre de color no dice el tono: el "Gris" de la Silla Cordoba es gris
     paloma y el mapa de hex lo pintaba oliváceo. Aquí se promedia una banda del
     centro de la foto de la variante, descartando el fondo casi blanco, y el
     resultado se cachea en localStorage. Es mejora progresiva: el círculo ya
     viene pintado del servidor, así que si esto no corre no se rompe nada.

     En cuanto se configuren los swatches nativos en el Admin, esos ganan y este
     muestreo deja de ejecutarse (el servidor no emite data-swatch-src). */

  const SWATCH_CACHE_KEY = 'brum:swatch:v2';
  let swatchCache = {};
  try { swatchCache = JSON.parse(localStorage.getItem(SWATCH_CACHE_KEY) || '{}'); } catch (e) { /* modo privado */ }

  let persistTimer;
  function persistSwatchCache() {
    clearTimeout(persistTimer);
    persistTimer = setTimeout(() => {
      try { localStorage.setItem(SWATCH_CACHE_KEY, JSON.stringify(swatchCache)); } catch (e) { /* cuota llena */ }
    }, 500);
  }

  function hueOf(r, g, b) {
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const d = max - min;
    if (!d) return -1;
    let h;
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    return h < 0 ? h + 360 : h;
  }

  function toHex(rgb) {
    return '#' + rgb.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('');
  }

  /* Color DOMINANTE, no promedio.
     El promedio aritmético falla: en una foto de estudio la mayor parte del
     encuadre es fondo claro y patas metálicas, así que promediar arrastra todo
     hacia el mismo gris pálido — el azul de la Silla Escocia salía gris y el
     negro salía gris medio. Aquí se busca el grupo de píxeles más numeroso:
     primero por tono, para telas con color; si no hay tono dominante, la pieza
     es acromática (negro, gris, crema) y se usa el grupo de luminosidad más
     numeroso. */
  function dominantColor(img) {
    const w = 48, h = 24;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);

    let data;
    try {
      data = ctx.getImageData(0, 0, w, h).data;
    } catch (e) {
      return null; // canvas contaminado: sin CORS no se puede leer
    }

    const HUE_BUCKETS = 24; // 15° cada uno
    const LIGHT_BINS = 12;
    // Por cada tono, un histograma de luminosidad. Promediar el cubo de tono
    // completo aclaraba el resultado: los píxeles del borde antialiasado son
    // navy mezclado con el fondo blanco, conservan el tono azul y pasan el
    // filtro de saturación, pero son mucho más claros que la tela. Con el modo
    // de luminosidad gana el interior de la pieza, que es el color de verdad.
    const hueLight = Array.from({ length: HUE_BUCKETS }, () =>
      Array.from({ length: LIGHT_BINS }, () => [0, 0, 0, 0])
    );
    const hueCount = new Array(HUE_BUCKETS).fill(0);
    const lightSum = Array.from({ length: LIGHT_BINS }, () => [0, 0, 0, 0]);
    let allSum = [0, 0, 0, 0];
    let bodyCount = 0;

    for (let i = 0; i < data.length; i += 4) {
      const R = data[i], G = data[i + 1], B = data[i + 2], A = data[i + 3];
      if (A < 128) continue;
      allSum[0] += R; allSum[1] += G; allSum[2] += B; allSum[3]++;

      const max = Math.max(R, G, B);
      const min = Math.min(R, G, B);
      const light = (max + min) / 510;      // 0..1
      const sat = max ? (max - min) / max : 0; // saturación HSV

      if (light > 0.92) continue; // fondo del estudio
      bodyCount++;

      const li = Math.min(LIGHT_BINS - 1, Math.floor(light * LIGHT_BINS));
      lightSum[li][0] += R; lightSum[li][1] += G; lightSum[li][2] += B; lightSum[li][3]++;

      if (sat >= 0.18 && light >= 0.10 && light <= 0.88) {
        const hue = hueOf(R, G, B);
        if (hue >= 0) {
          const hi = Math.floor(hue / (360 / HUE_BUCKETS)) % HUE_BUCKETS;
          const bin = hueLight[hi][li];
          bin[0] += R; bin[1] += G; bin[2] += B; bin[3]++;
          hueCount[hi]++;
        }
      }
    }

    if (!allSum[3]) return null;

    // 1. Tela con color: el tono más numeroso, si tiene masa suficiente para no
    //    ser un reflejo o el borde dorado de una pata. Dentro de ese tono se usa
    //    la luminosidad más frecuente, no el promedio: el azul marino salía
    //    celeste porque el borde antialiasado (navy mezclado con el fondo
    //    blanco) conserva el tono y aclaraba el promedio.
    let topHue = -1;
    for (let i = 0; i < HUE_BUCKETS; i++) {
      if (topHue < 0 || hueCount[i] > hueCount[topHue]) topHue = i;
    }
    if (topHue >= 0 && hueCount[topHue] >= bodyCount * 0.18) {
      const best = hueLight[topHue].reduce((a, c) => (c[3] > a[3] ? c : a), [0, 0, 0, 0]);
      if (best[3] > 0) {
        return toHex([best[0] / best[3], best[1] / best[3], best[2] / best[3]]);
      }
    }
    let best;

    // 2. Pieza acromática: el grupo de luminosidad más numeroso. Para una silla
    //    negra gana el grupo oscuro; para una gris, el medio.
    best = lightSum.reduce((a, c) => (c[3] > a[3] ? c : a), [0, 0, 0, 0]);
    if (best[3] > 0 && best[3] >= bodyCount * 0.12) {
      return toHex([best[0] / best[3], best[1] / best[3], best[2] / best[3]]);
    }

    // 3. Pieza blanca o crema: casi todo se descartó como fondo porque la pieza
    //    ES así de clara. Se promedia todo.
    return toHex([allSum[0] / allSum[3], allSum[1] / allSum[3], allSum[2] / allSum[3]]);
  }

  function paintSwatch(el) {
    const src = el.dataset.swatchSrc;
    if (!src || el.dataset.swatchDone) return;
    el.dataset.swatchDone = '1';

    if (swatchCache[src]) {
      el.style.setProperty('--swatch', swatchCache[src]);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => {
      const hex = dominantColor(img);
      if (!hex) return;
      el.style.setProperty('--swatch', hex);
      swatchCache[src] = hex;
      persistSwatchCache();
    };
    img.src = src;
  }

  // Ahorro de datos activado: se queda el color del mapa, que ya está pintado.
  const saveData = navigator.connection && navigator.connection.saveData;
  let swatchObserver = null;
  if (!saveData && 'IntersectionObserver' in window) {
    swatchObserver = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        swatchObserver.unobserve(en.target);
        paintSwatch(en.target);
      });
    }, { rootMargin: '200px' });
  }

  // Solo se muestrea lo que está por entrar en pantalla: en una rejilla de 24
  // productos son ~96 muestras y no tiene sentido pedirlas todas de golpe.
  function initSwatches(root) {
    (root || document).querySelectorAll('.swatch[data-swatch-src]:not([data-swatch-done])').forEach((el) => {
      if (swatchObserver) swatchObserver.observe(el);
      else paintSwatch(el);
    });
  }
  // global.js es defer, así que al ejecutarse el DOM ya está parseado.
  initSwatches(); initCardFit();

  /* ---------- muestras de color en la tarjeta ---------- */

  class ColorSwatches extends HTMLElement {
    connectedCallback() {
      this.card = this.closest('.card');
      if (!this.card) return;
      this.img = this.card.querySelector('.card__img:not(.card__img--hover)');
      this.links = [...this.card.querySelectorAll('[data-card-href]')];
      this.buttons = [...this.querySelectorAll('[data-swatch]')];
      if (!this.buttons.length) return;

      this.active = this.buttons.find((b) => b.classList.contains('is-active')) || this.buttons[0];
      this.buttons.forEach((btn) => {
        btn.addEventListener('click', () => this.select(btn));
        btn.addEventListener('mouseenter', () => this.paint(btn));
        btn.addEventListener('focus', () => this.paint(btn));
      });
      // Al salir, vuelve al color elegido (no al de la última previsualización).
      this.addEventListener('mouseleave', () => this.paint(this.active));
    }
    paint(btn) {
      if (!btn || !this.img || !btn.dataset.img) return;
      this.img.src = btn.dataset.img;
      if (btn.dataset.srcset) this.img.srcset = btn.dataset.srcset;
      fitCardImage(this.img);
      this.card.classList.add('card--picked');
    }
    select(btn) {
      this.active = btn;
      this.buttons.forEach((b) => {
        const on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.paint(btn);
      if (btn.dataset.url) this.links.forEach((a) => { a.href = btn.dataset.url; });
    }
  }
  if (!customElements.get('color-swatches')) customElements.define('color-swatches', ColorSwatches);

  /* ---------- galería ---------- */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('.gallery__main');
      this.thumbs = [...this.querySelectorAll('.gallery__thumb')];
      this.slides = [...this.querySelectorAll('.gallery__slide')];

      // Prueba A/B de la foto principal: en la rama B el CSS adelanta la foto de
      // ambiente con 'order', así que el orden visual deja de coincidir con el
      // del DOM. syncActive resuelve por índice contra el scroll, de modo que sin
      // esta lista ordenada marcaría la miniatura equivocada.
      const promoted = this.querySelector('.gallery__slide[data-ab-promote]');
      this.abPromoted = document.documentElement.classList.contains('ab-foto-b') ? promoted : null;
      this.ordered = this.abPromoted
        ? [this.abPromoted, ...this.slides.filter((s) => s !== this.abPromoted)]
        : this.slides;
      this.thumbs.forEach((t) => {
        t.addEventListener('click', (e) => {
          e.preventDefault();
          const slide = this.querySelector(`.gallery__slide[data-media-id="${t.dataset.mediaId}"]`);
          this.goTo(slide, true);
        });
      });
      // Respaldo para ventanas angostas donde las miniaturas siguen en carrusel:
      // traduce la rueda vertical a desplazamiento horizontal, porque con la
      // barra oculta no habría forma de llegar a las fotos de la derecha.
      const strip = this.querySelector('.gallery__thumbs');
      if (strip) {
        strip.addEventListener('wheel', (e) => {
          if (strip.scrollWidth <= strip.clientWidth) return;
          if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
          e.preventDefault();
          strip.scrollLeft += e.deltaY;
        }, { passive: false });
      }

      if (this.main && this.thumbs.length) {
        this.main.addEventListener('scroll', () => {
          clearTimeout(this.t);
          this.t = setTimeout(() => this.syncActive(), 80);
        }, { passive: true });
        this.syncActive();
      }
      this.jumpToInitial();
    }
    // Abre en la foto de la variante seleccionada, sin animación.
    jumpToInitial() {
      const id = this.dataset.initialMedia;
      if (!id || !this.main) return;
      // En la rama B la foto de ambiente ya está en el primer cuadro. Saltar a la
      // de la variante la sacaría de vista antes de que nadie la viera, que es
      // exactamente lo que la prueba quiere medir.
      if (this.abPromoted) return;
      const slide = this.querySelector(`.gallery__slide[data-media-id="${id}"]`);
      if (!slide || slide === this.ordered[0]) return;
      this.goTo(slide, false);
    }
    // El destino se calcula por ÍNDICE, nunca con slide.offsetLeft. offsetLeft se
    // mide contra el offsetParent, y ningún ancestro de la diapositiva está
    // posicionado, así que es el <body>: el valor arrastra la x de la galería en
    // la página. En escritorio ancho ese sobrante pasa de media diapositiva y el
    // scroll-snap cae en la SIGUIENTE foto — y como las fotos vienen agrupadas
    // por color, era el primer plano del color de junto (la Mesa Río en Greige
    // enseñaba la negra, 7-sep). En móvil el sobrante son los 20 px del
    // contenedor y el snap regresaba a la correcta, por eso solo se veía en
    // escritorio. Es el inverso exacto de syncActive.
    goTo(slide, smooth) {
      if (!slide || !this.main) return;
      const i = this.ordered.indexOf(slide);
      if (i < 0) return;
      this.main.scrollTo({ left: i * this.main.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
    }
    syncActive() {
      const i = Math.round(this.main.scrollLeft / this.main.clientWidth);
      const slide = this.ordered[i];
      if (!slide) return;
      this.thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.mediaId === slide.dataset.mediaId));
    }
  }
  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);

  /* ---------- sticky ATC ---------- */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const target = document.querySelector('[data-atc-anchor]');
      if (!target || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(
        ([entry]) => this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0),
        { threshold: 0 }
      );
      io.observe(target);
    }
  }
  if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);

  /* ---------- filtros y orden sin recargar ---------- */

  function collectionRoot() {
    return document.querySelector('[data-section-root][data-section-id]');
  }

  // Reemplaza solo [data-results]: el drawer de filtros queda intacto, así que
  // no se pierde el foco ni el estado de los acordeones mientras se filtra.
  async function renderCollection(url, { push = true } = {}) {
    const root = collectionRoot();
    const results = root && root.querySelector('[data-results]');
    if (!results) { window.location.href = url; return; }

    results.setAttribute('aria-busy', 'true');
    results.classList.add('is-loading');

    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', root.dataset.sectionId);

    try {
      const res = await fetch(target.toString());
      if (!res.ok) throw new Error(res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('[data-results]');
      if (!fresh) throw new Error('sin resultados en la respuesta');

      // El presupuesto de páginas automáticas se repone ANTES de insertar: el
      // <coll-mas> que viene en el HTML nuevo se conecta durante esta línea y
      // ya lee el valor repuesto.
      masAuto = null;
      results.innerHTML = fresh.innerHTML;
      initSwatches(results); initCardFit(results);

      // Los conteos de los filtros cambian al filtrar, pero reemplazar el panel
      // completo resetearía los acordeones: solo se actualizan los números.
      const freshCounts = [...doc.querySelectorAll('[data-filter-body] .filter-check__count')];
      const liveCounts = [...document.querySelectorAll('[data-filter-body] .filter-check__count')];
      if (freshCounts.length === liveCounts.length) {
        liveCounts.forEach((el, i) => { el.textContent = freshCounts[i].textContent; });
      }

      if (push) {
        const clean = new URL(url, window.location.origin);
        clean.searchParams.delete('section_id');
        window.history.pushState({ brumFilter: true }, '', clean.toString());
      }

      const count = results.querySelector('.toolbar__count');
      if (count) announce(count.textContent.trim());
    } catch (err) {
      window.location.href = url; // si falla el fetch, navegación normal
    } finally {
      results.setAttribute('aria-busy', 'false');
      results.classList.remove('is-loading');
    }
  }

  /* «En preventa» se pinta dentro de «Disponibilidad» (main-collection.liquid),
     pero es OTRO filtro de Shopify, y dos filtros distintos se cruzan (Y), no se
     suman (O): con «Disponible» (antes «En existencia») y «En preventa» marcados
     salían sólo las de preventa, que Shopify también cuenta como en
     existencia. Dentro del grupo se eligen de a uno: marcar uno desmarca al
     otro (9-oct). */
  const FILTRO_DISPONIBILIDAD = 'filter.v.availability';
  const FILTRO_PREVENTA = 'filter.p.m.preventa.activa';

  /* «Color disponible» (9-oct): el color sale de las versiones y Shopify lo
     cruza con la disponibilidad en la MISMA versión (probado: «Beige» daba 48
     sillas, la Módena entre ellas, que sólo tiene el beige agotado; «Beige» +
     «Disponible», 36 y sin ella). Por eso elegir un color marca también
     «Disponible», salvo que ya haya algo elegido en «Disponibilidad». */
  const FILTRO_COLOR = 'filter.v.option.color';

  class FilterForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('change', (e) => {
        if (e.target.matches('[data-price-input]')) return; // el precio, sólo al aplicar
        const t = e.target;
        if (t.checked && (t.name === FILTRO_PREVENTA || t.name === FILTRO_DISPONIBILIDAD)) {
          const otro = t.name === FILTRO_PREVENTA ? FILTRO_DISPONIBILIDAD : FILTRO_PREVENTA;
          this.form.querySelectorAll(`input[name="${otro}"]`).forEach((i) => { i.checked = false; });
        }
        if (t.checked && t.name === FILTRO_COLOR) {
          const grupo = [...this.form.querySelectorAll(`input[name="${FILTRO_DISPONIBILIDAD}"], input[name="${FILTRO_PREVENTA}"]`)];
          const disponible = this.form.querySelector(`input[name="${FILTRO_DISPONIBILIDAD}"][value="1"]`);
          if (disponible && !disponible.disabled && !grupo.some((i) => i.checked)) disponible.checked = true;
        }
        this.apply();
      });
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.apply();
        // Con el precio se aplica desde el botón: cerrar confirma la acción.
        const drawer = this.closest('x-drawer');
        if (drawer && drawer.close) drawer.close();
      });
    }
    apply() {
      const params = new URLSearchParams(new FormData(this.form));
      [...params.keys()].forEach((k) => {
        const values = params.getAll(k).filter((v) => v !== '');
        params.delete(k);
        values.forEach((v) => params.append(k, v));
      });
      renderCollection(`${window.location.pathname}?${params.toString()}`);
    }
  }
  if (!customElements.get('filter-form')) customElements.define('filter-form', FilterForm);

  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-sort-select]')) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', e.target.value);
      url.searchParams.delete('page');
      renderCollection(url.toString());
    }
  });

  // Quitar un chip de filtro activo, limpiar todo y paginar: también sin recarga.
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-results] .chip, [data-results] .pagination a');
    if (!link || !link.href) return;
    e.preventDefault();
    renderCollection(link.href);
    if (link.matches('.pagination a')) window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  // El botón Atrás debe deshacer el filtro, no salir de la colección.
  window.addEventListener('popstate', () => {
    if (collectionRoot()) renderCollection(window.location.href, { push: false });
  });

  /* ---------- scroll infinito ---------- */

  /* El presupuesto de páginas que se cargan solas vive FUERA del elemento a
     propósito: cada página que llega trae su propio <coll-mas> y sustituye al
     anterior, así que un contador guardado dentro se repondría en cada carga y
     el scroll no pararía nunca. Solo renderCollection lo devuelve a null, que
     es cuando la lista de verdad empieza de cero. */
  let masAuto = null;
  let masCargando = false;

  class CollMas extends HTMLElement {
    connectedCallback() {
      const root = collectionRoot();
      if (!root || root.dataset.paginacion !== 'infinito') return;
      if (masAuto === null) masAuto = Number(root.dataset.autoPaginas) || 2;

      this.btn = this.querySelector('.coll-mas__btn');
      if (!this.btn) return;

      /* Destapar aquí y no en Liquid es lo que hace la mejora progresiva: sin
         JavaScript no aparece ningún botón que no llevaría a ningún lado y se
         queda la paginación numerada, que además es la que siguen los
         buscadores. */
      this.hidden = false;
      const pag = root.querySelector('[data-results] .pagination');
      if (pag) pag.classList.add('pagination--oculta');

      this.btn.addEventListener('click', () => this.cargar(false));
      this.armar();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
    }

    // Con presupuesto, un centinela carga sola la siguiente página. Sin él,
    // queda el botón: es lo que deja llegar al texto y a las preguntas de
    // abajo, que con scroll sin freno quedan fuera de alcance.
    armar() {
      if (masAuto <= 0 || !('IntersectionObserver' in window)) return;
      this.btn.hidden = true;
      this.io = new IntersectionObserver((entradas) => {
        if (!entradas.some((en) => en.isIntersecting)) return;
        this.io.disconnect();
        this.cargar(true);
      }, { rootMargin: '600px 0px' }); // se adelanta para que no se vea el hueco
      this.io.observe(this);
    }

    async cargar(auto) {
      const root = collectionRoot();
      const results = root && root.querySelector('[data-results]');
      const grid = results && results.querySelector('.grid');
      if (masCargando || !this.dataset.url || !grid) return;

      masCargando = true;
      if (auto) masAuto -= 1;
      this.classList.add('is-loading');
      this.btn.disabled = true;
      // [data-results] es una región aria-live: sin esto un lector de pantalla
      // cantaría las 48 tarjetas de una en una mientras entran.
      results.setAttribute('aria-busy', 'true');

      const destino = new URL(this.dataset.url, window.location.origin);
      destino.searchParams.set('section_id', root.dataset.sectionId);

      try {
        const res = await fetch(destino.toString());
        if (!res.ok) throw new Error(res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const rejillaNueva = doc.querySelector('[data-results] .grid');
        if (!rejillaNueva) throw new Error('sin rejilla en la respuesta');

        /* Los agotados van al final de TODA la lista, no al final de cada
           página. Concatenando a secas aparecería una tanda de agotados cada
           48 productos, en medio del scroll, y la regla de la sección dejaría
           de cumplirse justo donde más se nota. */
        const disponibles = document.createDocumentFragment();
        const agotados = document.createDocumentFragment();
        [...rejillaNueva.children].forEach((tarjeta) => {
          (tarjeta.hasAttribute('data-agotado') ? agotados : disponibles).appendChild(tarjeta);
        });
        // Antes de insertar: los fragmentos se vacían al hacerlo.
        const primerNuevo = disponibles.firstElementChild || agotados.firstElementChild;
        const primerAgotado = grid.querySelector('[data-agotado]');
        if (primerAgotado) grid.insertBefore(disponibles, primerAgotado);
        else grid.appendChild(disponibles);
        grid.appendChild(agotados);
        initSwatches(grid); initCardFit(grid);

        // La paginación oculta se mantiene al día: sigue siendo el camino de
        // los buscadores y el de quien recarga sin JavaScript.
        const pagVieja = results.querySelector('.pagination');
        const pagNueva = doc.querySelector('[data-results] .pagination');
        if (pagVieja && pagNueva) {
          pagNueva.classList.add('pagination--oculta');
          pagVieja.replaceWith(pagNueva);
        } else if (pagVieja) {
          pagVieja.remove();
        }

        /* replaceState y no pushState: con push, Atrás desharía una a una las
           páginas que se cargaron solas y salir de la colección costaría tantos
           toques como páginas hubiera bajado el visitante. */
        const limpia = new URL(this.dataset.url, window.location.origin);
        limpia.searchParams.delete('section_id');
        window.history.replaceState({ brumPagina: true }, '', limpia.toString());

        if (!auto && primerNuevo) {
          // Con clic el foco tiene que viajar: si se queda en el botón, que
          // además se acaba de mover, con teclado no hay señal de qué pasó.
          primerNuevo.setAttribute('tabindex', '-1');
          primerNuevo.focus({ preventScroll: true });
        }

        // El relevo trae su propia URL y se rearma solo al conectarse. Si no
        // viene, era la última página.
        const relevo = doc.querySelector('[data-results] coll-mas');
        if (relevo) this.replaceWith(relevo);
        else this.remove();
      } catch (err) {
        /* Si falla la carga se deja de insistir solo y vuelve el botón: el
           enlace de la paginación sigue vivo detrás por si tampoco responde. */
        masAuto = 0;
        this.classList.remove('is-loading');
        this.btn.disabled = false;
        this.btn.hidden = false;
      } finally {
        results.setAttribute('aria-busy', 'false');
        masCargando = false;
      }
    }
  }
  if (!customElements.get('coll-mas')) customElements.define('coll-mas', CollMas);

  /* ---------- estado de carga en envíos nativos ---------- */

  // Newsletter y contacto recargan la página al enviar: sin esto no hay ninguna
  // señal de que el clic sirvió, y la gente vuelve a picar el botón.
  document.addEventListener('submit', (e) => {
    const form = e.target;
    // El formulario de producto es AJAX y maneja su propio estado.
    if (form.closest('product-form') || form.closest('filter-form')) return;
    const btn = form.querySelector('button[type="submit"].btn');
    if (!btn || btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-disabled', 'true');
  });

  /* ---------- búsqueda predictiva ---------- */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-psearch-input]');
      this.panel = this.querySelector('[data-psearch-results]');
      if (!this.input || !this.panel) return;

      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        const term = this.input.value.trim();
        if (term.length < 2) { this.hide(); return; }
        // 250 ms: suficiente para no disparar una petición por cada tecla.
        this.timer = setTimeout(() => this.search(term), 250);
      });

      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { this.hide(); this.input.blur(); }
        if (e.key === 'ArrowDown') {
          const first = this.panel.querySelector('.psearch__item');
          if (first) { e.preventDefault(); first.focus(); }
        }
      });

      this.panel.addEventListener('keydown', (e) => {
        const items = [...this.panel.querySelectorAll('.psearch__item')];
        const i = items.indexOf(document.activeElement);
        if (e.key === 'ArrowDown' && i > -1 && i < items.length - 1) { e.preventDefault(); items[i + 1].focus(); }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (i > 0) items[i - 1].focus(); else this.input.focus();
        }
        if (e.key === 'Escape') { this.hide(); this.input.focus(); }
      });

      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.hide();
      });
    }

    async search(term) {
      // Aborta la petición anterior: escribiendo rápido las respuestas pueden
      // llegar desordenadas y pintar resultados de un término ya viejo.
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = `${root}search/suggest?q=${encodeURIComponent(term)}`
        + '&resources[type]=product,collection,page'
        + '&resources[limit]=6'
        + '&section_id=predictive-search';

      this.classList.add('is-loading');
      try {
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('.shopify-section') || doc.body;
        this.panel.innerHTML = fresh.innerHTML.trim();
        initSwatches(this.panel); initCardFit(this.panel);
        this.show();
      } catch (err) {
        if (err.name !== 'AbortError') this.hide();
      } finally {
        this.classList.remove('is-loading');
      }
    }

    show() {
      if (!this.panel.innerHTML.trim()) return this.hide();
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }
    hide() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  /* ---------- ventana de entrega ---------- */

  /* La tienda vive en America/Mexico_City, que desde 2022 ya no tiene horario
     de verano: es UTC-6 fijo. Se trabaja con una fecha desplazada y sus
     getters UTC, así el cálculo sale igual desde cualquier zona: la promesa es
     la del almacén, no la del reloj del visitante. */
  const ENTREGA_TZ = -6 * 60;
  const ENTREGA_MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

  class BrumEntrega extends HTMLElement {
    connectedCallback() {
      /* Si algo falla se queda el texto que imprimió Liquid («Recibe en 5 a 9
         días hábiles»), que es cierto aunque la página venga del caché. */
      try { this.pinta(); } catch (e) { /* silencio */ }
    }

    pinta() {
      const min = parseInt(this.dataset.min, 10);
      const max = parseInt(this.dataset.max, 10);
      if (!(min > 0) || !(max >= min)) return;

      const habiles = (this.dataset.habiles || '1,2,3,4,5')
        .split(',').map((n) => parseInt(n, 10)).filter((n) => n >= 0 && n <= 6);
      if (!habiles.length) return;

      const festivos = new Set((this.dataset.festivos || '').split(',').filter(Boolean));
      const corte = (this.dataset.corte || '13:00').split(':');
      const corteMin = (parseInt(corte[0], 10) || 0) * 60 + (parseInt(corte[1], 10) || 0);

      const esHabil = (d) => habiles.includes(d.getUTCDay()) && !festivos.has(iso(d));
      const ahora = new Date(Date.now() + ENTREGA_TZ * 60000);
      const base = new Date(Date.UTC(ahora.getUTCFullYear(), ahora.getUTCMonth(), ahora.getUTCDate()));

      /* El reloj arranca hoy sólo si hoy se trabaja y todavía no pasó el corte;
         si no, empieza el siguiente día hábil. */
      let aTiempo = esHabil(base) && (ahora.getUTCHours() * 60 + ahora.getUTCMinutes()) < corteMin;
      if (!aTiempo) {
        let vueltas = 0;
        do { base.setUTCDate(base.getUTCDate() + 1); vueltas++; } while (!esHabil(base) && vueltas < 60);
      }

      /* Preventa (24-sep): el reloj arranca el día que la pieza llega al
         almacén —o el siguiente hábil, si cae en fin de semana o festivo— y no
         hoy. Es la misma cuenta de días hábiles que la de una pieza en stock, a
         propósito: dos reglas distintas acabarían prometiendo distinto. Sin
         aviso de la hora de corte, porque la fecha ya no depende de pedir hoy.
         Una llegada que ya pasó no se usa: el Liquid la bloquea, y esto sólo
         puede pasar con una ficha que se quedó abierta de un día para otro. */
      let envio = '';
      const llegada = /^(\d{4})-(\d{2})-(\d{2})$/.exec(this.dataset.desde || '');
      if (llegada) {
        const dia = new Date(Date.UTC(+llegada[1], +llegada[2] - 1, +llegada[3]));
        const hoy = new Date(Date.UTC(ahora.getUTCFullYear(), ahora.getUTCMonth(), ahora.getUTCDate()));
        if (dia.getTime() >= hoy.getTime()) {
          base.setTime(dia.getTime());
          let vueltas = 0;
          while (!esHabil(base) && vueltas < 60) { base.setUTCDate(base.getUTCDate() + 1); vueltas++; }
          aTiempo = false;
          envio = `${this.dataset.envio || 'Se envía a partir del'} `
            + `<b>${dia.getUTCDate()} de ${ENTREGA_MESES[dia.getUTCMonth()]}</b> · `;
        }
      }

      const suma = (dias) => {
        const d = new Date(base.getTime());
        let faltan = dias;
        let vueltas = 0;
        while (faltan > 0 && vueltas < 400) {
          d.setUTCDate(d.getUTCDate() + 1);
          vueltas++;
          if (esHabil(d)) faltan--;
        }
        return d;
      };

      const desde = suma(min);
      const hasta = suma(max);
      const mismoMes = desde.getUTCMonth() === hasta.getUTCMonth()
        && desde.getUTCFullYear() === hasta.getUTCFullYear();

      const rango = mismoMes
        ? `<b>${desde.getUTCDate()}</b> y el <b>${hasta.getUTCDate()} de ${ENTREGA_MESES[hasta.getUTCMonth()]}</b>`
        : `<b>${desde.getUTCDate()} de ${ENTREGA_MESES[desde.getUTCMonth()]}</b> y el `
          + `<b>${hasta.getUTCDate()} de ${ENTREGA_MESES[hasta.getUTCMonth()]}</b>`;

      /* En preventa la frase queda «Se envía a partir del 10 de octubre ·
         recibe entre el 19 y el 22 de octubre»: el prefijo pasa a minúscula
         porque ya no abre la oración. */
      const prefijo = this.dataset.prefijo || 'Recibe entre el';
      let html = envio
        ? `${envio}${prefijo.charAt(0).toLowerCase()}${prefijo.slice(1)} ${rango}`
        : `${prefijo} ${rango}`;
      /* El aviso del corte sólo se muestra mientras la fecha aún dependa de
         pedir hoy; pasado el corte sería una prisa falsa. */
      if (aTiempo && this.dataset.corteTexto) html += ` · ${this.dataset.corteTexto}`;
      /* El punto cierra la frase: en la ficha, justo después va la de envíos y
         devoluciones, en el mismo renglón. */
      this.innerHTML = `${html}.`;
    }
  }

  function iso(d) {
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${d.getUTCFullYear()}-${m}-${dd}`;
  }

  if (!customElements.get('brum-entrega')) customElements.define('brum-entrega', BrumEntrega);

  /* ---------- rastreo de pedido ---------- */

  /* Las paqueterías salen del histórico de envíos, no de un supuesto. De las
     tres, sólo Estafeta y Envia.com aceptan la guía en la dirección —probado
     contra sus rastreadores—; el de Paquetexpress se arma con JS y no lee
     parámetros, así que ahí se copia la guía y se abre su página. */
  const RASTREO = [
    { nombre: 'Paquetexpress', patron: /^23\d{10}$/, url: () => 'https://www.paquetexpress.com.mx/rastreo', copiar: true },
    { nombre: 'Estafeta', patron: /^\d{18}[A-Za-z0-9]{4}$/, url: (g) => `https://www.estafeta.com/rastrear-envio?guia=${encodeURIComponent(g)}` },
  ];
  const RASTREO_FALLBACK = { nombre: 'Envia.com', url: (g) => `https://envia.com/tracking?label=${encodeURIComponent(g)}` };

  class BrumRastreo extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-guia]');
      this.boton = this.querySelector('[data-ir]');
      this.pista = this.querySelector('[data-pista]');
      if (!this.input || !this.boton) return;

      this.boton.addEventListener('click', () => this.ir());
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); this.ir(); }
      });
    }

    limpia() {
      /* Las guías se dictan por teléfono y se pegan de correos: llegan con
         espacios, guiones y minúsculas. */
      return (this.input.value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    }

    detecta(guia) {
      return RASTREO.find((p) => p.patron.test(guia)) || RASTREO_FALLBACK;
    }

    dice(texto, paqueteria) {
      if (!this.pista) return;
      this.pista.textContent = (texto || '').replace('{paqueteria}', paqueteria || '');
    }

    ir() {
      const guia = this.limpia();
      if (!guia) {
        this.dice(this.dataset.vacio || 'Escribe el número de guía.');
        this.input.focus();
        return;
      }
      const dest = this.detecta(guia);
      /* La copia va antes de abrir la pestaña: después el foco se va y el
         portapapeles deja de estar disponible. No se espera la promesa para no
         perder el gesto del usuario, que es lo que autoriza el window.open. */
      if (dest.copiar && navigator.clipboard) {
        try { navigator.clipboard.writeText(guia); } catch (e) { /* silencio */ }
      }
      window.open(dest.url(guia), '_blank', 'noopener');
      this.dice(dest.copiar ? this.dataset.copiado : this.dataset.detectada, dest.nombre);
    }
  }
  if (!customElements.get('brum-rastreo')) customElements.define('brum-rastreo', BrumRastreo);

  /* ---------- WhatsApp flotante: el mensaje se arma al hacer clic ---------- */

  /* Escuchar el clic y no el cambio de variante es a propósito: el selector ya
     deja la versión elegida en el formulario, así que en el momento del clic
     todo lo que hace falta está en la página. Cero listeners en los colores.

     La liga NO se copia de la barra del navegador. Si el cliente llegó de una
     búsqueda, un carrusel o un anuncio, la barra trae cientos de letras de
     rastreo (_su_rec=…, pr_rec_id=…, fbclid=…) y eso le llegaba al asesor
     (17-oct-2026). Va sólo la ficha (`data-wa-url`) más el ?variant= de la
     versión elegida, y cada parte en su renglón:
       Hola, vengo de brum.mx y tengo una duda sobre:
       Sillón individual Melbourne (Gris)
       https://brum.mx/products/sillon-individual-melbourne?variant=…
     El «vengo de brum.mx y tengo una duda sobre» se queda igual: el bot lo
     usa para pasar el caso a Ventas (brum-chatbot, lib/brain.js). */
  document.addEventListener('click', (e) => {
    const a = e.target.closest ? e.target.closest('.wa-flota[data-wa-base]') : null;
    if (!a) return;
    try {
      const base = a.dataset.waBase || '';
      const prod = a.dataset.waProducto || '';
      if (!prod) return;                       /* fuera de la ficha, el genérico */

      /* Las opciones elegidas —Color, Tamaño— se leen del propio selector. */
      const opciones = [...document.querySelectorAll('variant-picker [data-opt-value]')]
        .map((el) => el.textContent.trim())
        .filter(Boolean)
        .join(' · ');

      const ficha = new URL(a.dataset.waUrl || location.pathname, location.origin);
      ficha.search = '';
      ficha.hash = '';
      const idInput = document.querySelector('.pdp__form [name="id"]');
      const variante = (idInput && idInput.value) || new URL(location.href).searchParams.get('variant');
      if (/^\d+$/.test(variante || '')) ficha.searchParams.set('variant', variante);

      const cabeza = /[:.!?]$/.test(base) ? base : `${base}:`;
      const url = new URL(a.href);
      url.searchParams.set(
        'text',
        `${cabeza}\n${prod}${opciones ? ` (${opciones})` : ''}\n${ficha}`
      );
      a.href = url.toString();
    } catch (err) { /* se queda el mensaje genérico */ }
  }, true);

  /* ---------- animación de entrada ---------- */

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('anim'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -40px' });
    document.querySelectorAll('[data-anim]').forEach((el) => io.observe(el));
  }
})();
